/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';

import Header from './Header';
import Message from './Message';
import TypingIndicator from './TypingIndicator';
import ErrorMessage from './ErrorMessage';
import ConfirmationCard from './ConfirmationCard';
import InputArea from './InputArea';

import './styles.css';

const Widget = ({ config, chat, entity, handlers, chatT }) => {
  const { title, placeholder, collapsedPlaceholder, entityType, summaryFields, supportsImageUpload } = config;
  const { messages = [], isLoading = false, error = null } = chat;
  const { data: entityData = null, isCreating = false } = entity;
  const { onSendMessage, onReset, onConfirmCreate, onMakeChanges, onDismissError } = handlers;
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current && isExpanded) {
      messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' })
    }
  }, [messages, isExpanded]);

  const handleExpand = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) return;

    if (!isExpanded) {
      handleExpand();
    }

    const message = inputValue.trim();
    setInputValue('');
    onSendMessage(message);
  };

  const handleMakeChanges = () => {
    onMakeChanges();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleReset = () => {
    setInputValue('');
    onReset();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`ai-chat-widget-floating ${isExpanded ? 'expanded' : ''}`}>
      {isExpanded && (
        <>
          <Header
            title={title}
            showReset={messages.length > 1}
            onReset={handleReset}
            onClose={() => setIsExpanded(false)}
            chatT={chatT}
          />

          <div className="ai-chat-widget-messages">
            {messages.map((msg, index) => (
              <Message key={index} message={msg} />
            ))}
            
            {isLoading && <TypingIndicator />}
            
            {error && <ErrorMessage error={error} onDismiss={onDismissError} />}
            
            {entityData && (
              <ConfirmationCard
                entityType={entityType}
                entityData={entityData}
                summaryFields={summaryFields}
                isCreating={isCreating}
                onMakeChanges={handleMakeChanges}
                onConfirm={(data, image) => onConfirmCreate(data, image)}
                chatT={chatT}
                supportsImageUpload={supportsImageUpload}
              />
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </>
      )}

      <InputArea
        isExpanded={isExpanded}
        placeholder={placeholder}
        collapsedPlaceholder={collapsedPlaceholder}
        inputValue={inputValue}
        inputRef={inputRef}
        isLoading={isLoading}
        onExpand={handleExpand}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onSend={handleSend}
      />
    </div>
  );
};

export default Widget;
